import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { VIDEOS, SITE } from "@/mock";

function YouTubeFacade({ v }) {
  const [play, setPlay] = useState(false);
  if (play) {
    return (
      <iframe
        className="h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${v.youtube}?autoplay=1&rel=0`}
        title={v.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        data-testid={`video-iframe-${v.slug}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setPlay(true)}
      aria-label={`Play ${v.title}`}
      data-testid={`video-play-${v.slug}`}
      className="group/play relative h-full w-full"
    >
      <img src={v.poster} alt="" loading="lazy" className={`h-full w-full ${v.orientation === "portrait" ? "object-cover" : "object-contain"}`} />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#D0021B] text-white shadow-xl transition-transform duration-200 group-hover/play:scale-110">
          <Play className="h-7 w-7 translate-x-0.5 fill-current" aria-hidden="true" />
        </span>
      </span>
    </button>
  );
}

export default function Videos() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Videos", path: "/videos" },
  ];
  return (
    <>
      <Seo
        title="Mattress Videos - See Our Brands in Action"
        description="Watch product and brand videos from MattressTown in Fort Lauderdale featuring Beautyrest Black, King Koil DualFlex, adjustable bases and more."
        path="/videos"
        jsonLd={breadcrumbJsonLd(crumbs)}
      />

      <section className="bg-neutral-900">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <span className="inline-block rounded-full bg-[#D0021B] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white">
            Video Gallery
          </span>
          <h1 className="mt-4 text-4xl font-bold uppercase leading-tight text-white sm:text-5xl">
            See Our Brands in Action
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-neutral-300">
            Go behind the comfort - explore the technology, craftsmanship and design of the premium
            mattress and adjustable-base brands we carry at MattressTown.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VIDEOS.map((v) => (
            <figure
              key={v.slug}
              data-testid={`video-card-${v.slug}`}
              className="group overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-shadow hover:shadow-lg"
            >
              <div className="flex h-72 items-center justify-center overflow-hidden bg-black sm:h-80 lg:h-96">
                {v.youtube ? (
                  <YouTubeFacade v={v} />
                ) : (
                <video
                  className="h-full w-full object-contain"
                  controls
                  playsInline
                  preload="none"
                  poster={v.poster}
                  data-testid={`video-player-${v.slug}`}
                >
                  <source src={v.src} type="video/mp4" />
                </video>
                )}
              </div>
              <figcaption className="p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-[#D0021B]">{v.brand}</p>
                <h2 className="mt-1 text-lg font-bold text-neutral-900">{v.title}</h2>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/mattresses"
            data-testid="videos-shop-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-9 py-4 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016]"
          >
            Shop the Collection
          </Link>
          <div className="mt-6">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-neutral-700 hover:text-[#D0021B]">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
